import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { ArrowDropDownOutlined } from "@mui/icons-material";
import Check from "@mui/icons-material/Check";
import { CircularProgress, Menu, MenuItem, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { Box } from "@mui/system";
import { AppContext } from "App";
import { getBudgetYears } from "components/Budget/helpers";
import DropdownSelector from "components/DropdownSelector";
import { ArcPlcIcon, BudgetIcon, InsuranceIcon } from "components/Icons/Icons";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import { useSnackbar } from "notistack";

import { UsersApiHelperForMember } from "api/ApiHelper";
import { REPORT_SECTION_OPTIONS, REPORT_SECTION_TASK_PROPERTY } from "../../../constants/reportSections";
import { ReportsIcon } from "../../Icons/Icons";

export const TASK_GENERATE_REPORT_AGENCY = "Generate_Report_Agency";

/**
 * UsersApiHelperForMember.callPost runs @datanac/datanac-api-toolkit normalizeResponse on array bodies.
 * That function lowercases every key on each row (taskName→taskname, isProcessed→isprocessed).
 * Task/search returns an array → fields must be read with fallbacks. Task/enqueue returns a single object → unchanged camelCase.
 */
const taskProcessed = (t) => !!(t?.isProcessed ?? t?.isprocessed);
const taskCreateDate = (t) => t?.createDate ?? t?.createdate;
const taskStartDate = (t) => t?.startDate ?? t?.startdate;

const extractPdfUrlFromTask = (task) => {
  if (!task) return null;
  return typeof task.properties?.url === "string" ? task.properties.url : null;
};

/** Task/search often returns multiple rows per id (history). Prefer completed + URL, then latest snapshot. */
const resolveTaskSnapshotById = (tasks, taskId) => {
  if (!taskId || !Array.isArray(tasks)) return null;
  const idStr = String(taskId);
  const rows = tasks.filter((t) => String(t.id ?? t.Id) === idStr);
  if (!rows.length) return null;

  const processedWithUrl = rows.find((t) => taskProcessed(t) && extractPdfUrlFromTask(t));
  if (processedWithUrl) return processedWithUrl;

  const processed = rows.find((t) => taskProcessed(t));
  if (processed) return processed;

  return [...rows].sort(
    (a, b) => new Date(taskCreateDate(b) || 0) - new Date(taskCreateDate(a) || 0)
  )[0];
};

export default function ProducerAdvisoryReport() {
  const { globalState, globalDispatch } = useContext(AppContext);
  const { enqueueSnackbar } = useSnackbar();

  const [currentYear, setCurrentYear] = useState(globalState?.crop_year || getBudgetYears()?.[0] || "");

  const [loading, setLoading] = useState(false);
  const [isEnqueuingTask, setIsEnqueuingTask] = useState(false);
  const [selectedSections, setSelectedSections] = useState(REPORT_SECTION_OPTIONS.map((section) => section.id));

  const [pdfReport, setPdfReport] = useState("");
  const [pdfTask, setPdfTask] = useState(null);

  const pollingTimeoutRef = useRef(null);
  const isCancelledRef = useRef(false);

  const clearPolling = useCallback(() => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => clearPolling, [clearPolling]);
  useEffect(() => {
    if (globalState?.crop_year !== currentYear) {
      globalDispatch({ type: "update", payload: { key: "crop_year", value: currentYear } });
    }
  }, [currentYear, globalDispatch, globalState?.crop_year]);

  const searchPdfTasks = useCallback(async () => {
    const requestBody = {
      tenantID: import.meta.env.VITE_RGCHAT_TENANTID_TILLEY,
      userID: globalState?.producer_token,
      sessionID: "00000000-0000-0000-0000-000000000000",
      accessKey: "00000000-0000-0000-0000-000000000000",
      properties: {
        producer_token: globalState?.producer_token,
        agent_token: globalState?.agent_token,
        year: currentYear == null ? "" : String(currentYear),
      },
    };

    try {
      const tasks = await UsersApiHelperForMember.callPost("Task/search", requestBody, globalState);
      return Array.isArray(tasks) ? tasks : [];
    } catch (err) {
      console.error("Error searching pdf tasks (network):", err);
      return [];
    }
  }, [currentYear, globalState]);

  const startPdfTaskPolling = useCallback(
    (taskId) => {
      clearPolling();
      if (!taskId) return;

      setLoading(true);

      let attempt = 0;
      const maxAttempts = 60;
      /** ~1s between polls for the first ~15s, then exponential backoff. */
      const fastPollAttempts = 15;
      const fastIntervalMs = 1000;
      const slowBaseMs = 2000;
      const slowMaxMs = 16000;

      const scheduleNextPoll = () => {
        if (attempt >= maxAttempts) {
          setLoading(false);
          enqueueSnackbar("PDF generation timed out. Please try again.", { variant: "warning" });
          return;
        }

        let nextInterval;
        if (attempt < fastPollAttempts) {
          nextInterval = fastIntervalMs;
        } else {
          const slowIdx = attempt - fastPollAttempts;
          nextInterval = Math.min(slowBaseMs * Math.pow(2, slowIdx), slowMaxMs);
        }
        attempt++;

        pollingTimeoutRef.current = setTimeout(async () => {
          try {
            const tasks = await searchPdfTasks();
            const task = resolveTaskSnapshotById(tasks, taskId);

            if (task) {
              setPdfTask(task);

              if (taskProcessed(task)) {
                const url = extractPdfUrlFromTask(task);

                if (url) {
                  setPdfReport(url);
                } else {
                  enqueueSnackbar("Report completed, but no PDF URL was returned.", { variant: "warning" });
                }

                setLoading(false);
                clearPolling();
                return;
              }
            }

            scheduleNextPoll();
          } catch (e) {
            console.error("PDF polling error:", e);
            setLoading(false);
            clearPolling();
            enqueueSnackbar("Polling failed.", { variant: "error" });
          }
        }, nextInterval);
      };

      scheduleNextPoll();
    },
    [clearPolling, enqueueSnackbar, searchPdfTasks]
  );

  const enqueueGeneratePdfTask = async () => {
    isCancelledRef.current = false;
    setIsEnqueuingTask(true);
    setPdfReport("");
    setPdfTask(null);
    setLoading(false);
    clearPolling();

    try {
      const requestBody = {
        tenantID: import.meta.env.VITE_RGCHAT_TENANTID_TILLEY,
        userID: globalState?.producer_token,
        sessionID: "00000000-0000-0000-0000-000000000000",
        accessKey: "00000000-0000-0000-0000-000000000000",
        taskName: TASK_GENERATE_REPORT_AGENCY,
        promptName: null,
        userMessage: null,
        media: [],
        properties: {
          producer_token: globalState?.producer_token,
          agent_token: globalState?.agent_token,
          year: currentYear == null ? "" : String(currentYear),
          [REPORT_SECTION_TASK_PROPERTY]: selectedSections
            .map((sectionId) => REPORT_SECTION_OPTIONS.find((section) => section.id === sectionId)?.daemonValue)
            .filter(Boolean)
            .join(","),
        },
      };

      const task = await UsersApiHelperForMember.callPost("Task/enqueue", requestBody, globalState);

      if (!isCancelledRef.current) {
        startPdfTaskPolling(task?.id);
      }
    } catch {
      if (!isCancelledRef.current) {
        enqueueSnackbar("Failed to start pdf generation", { variant: "error" });
      }
    } finally {
      if (!isCancelledRef.current) {
        setIsEnqueuingTask(false);
      }
    }
  };

  const handleCancel = () => {
    isCancelledRef.current = true;
    clearPolling();
    setLoading(false);
    setIsEnqueuingTask(false);
    enqueueSnackbar("PDF generation canceled", { variant: "info" });
  };

  const sanitizePdfUrl = (url) => {
    if (!url || typeof url !== "string") return null;
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== "https:" || !parsed.hostname.startsWith("datanac-users-api-files")) {
        console.warn("Untrusted PDF URL blocked:", url);
        return null;
      }
      return url;
    } catch {
      return null;
    }
  };

  const safePdfUrl = sanitizePdfUrl(pdfReport);
  const selectedSectionLabel = selectedSections.length
    ? `${selectedSections.length} selected`
    : "None selected (all sections)";
  const reportSectionIcons = {
    budget: <BudgetIcon sx={{ fontSize: 18 }} />,
    arcPlc: <ArcPlcIcon sx={{ fontSize: 18 }} />,
    insurance: <InsuranceIcon sx={{ fontSize: 18 }} />,
    reports: <ReportsIcon sx={{ fontSize: 18 }} />,
  };

  return (
    <>
      <WorkspaceHeader title="Producer Advisory Report" category={{ title: "Reports", icon: <ReportsIcon /> }} />

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <DropdownSelector
          value={currentYear}
          onChange={setCurrentYear}
          options={getBudgetYears()}
          disabled={loading || isEnqueuingTask}
          popupId="producer-advisory-year-menu"
        />

        <PopupState variant="popover" popupId="report-sections-menu">
          {(popupState) => (
            <>
              <Button variant="outlined" {...bindTrigger(popupState)} disabled={loading || isEnqueuingTask}>
                Sections: {selectedSectionLabel} <ArrowDropDownOutlined />
              </Button>
              <Menu {...bindMenu(popupState)}>
                {REPORT_SECTION_OPTIONS.map((section) => {
                  const isSelected = selectedSections.includes(section.id);
                  return (
                    <MenuItem
                      key={section.id}
                      onClick={() => {
                        setSelectedSections((prev) =>
                          prev.includes(section.id)
                            ? prev.filter((value) => value !== section.id)
                            : [...prev, section.id]
                        );
                      }}
                      disabled={loading || isEnqueuingTask}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          width: "100%",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {reportSectionIcons[section.icon] || null}
                          <Typography variant="body2">{section.label}</Typography>
                        </Box>
                        {isSelected ? <Check fontSize="small" /> : null}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Menu>
            </>
          )}
        </PopupState>

        <Button
          variant="contained"
          color="primary"
          onClick={enqueueGeneratePdfTask}
          disabled={isEnqueuingTask || loading}
          startIcon={isEnqueuingTask || loading ? <CircularProgress size={16} /> : null}
        >
          {isEnqueuingTask
            ? "Starting..."
            : loading
              ? "Generating..."
              : pdfReport
                ? "Generate New Report"
                : "Generate Report"}
        </Button>

        {(loading || isEnqueuingTask) && (
          <Button variant="contained" color="secondary" onClick={handleCancel}>
            Cancel
          </Button>
        )}

        {safePdfUrl && !loading && (
          <Button variant="outlined" href={safePdfUrl} download={`ProducerAdvisoryReport_${currentYear}.pdf`}>
            Download PDF
          </Button>
        )}
      </Box>

      <Box
        sx={{
          height: "calc(100vh - 48px)",
          display: "flex",
          alignItems: pdfReport ? "center" : "flex-start",
          justifyContent: pdfReport ? "center" : "flex-start",
          bgcolor: "background.paper",
          borderRadius: 1,
          p: 2,
        }}
      >
        {safePdfUrl ? (
          <iframe
            src={safePdfUrl}
            type="application/pdf"
            style={{ width: "100%", height: "100%", border: "none", backgroundColor: "white" }}
            title="Document Preview"
            onError={() => console.error("Iframe failed to load")}
          />
        ) : isEnqueuingTask ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={16} />
            <Typography color="text.secondary" variant="body2">
              Starting...
            </Typography>
          </Box>
        ) : loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={16} />
            <Typography color="text.secondary" variant="body2">
              Generating...
              {taskStartDate(pdfTask)
                ? ` Started ${new Date(taskStartDate(pdfTask)).toLocaleTimeString()}`
                : ""}
            </Typography>
          </Box>
        ) : (
          <Typography color="text.secondary" variant="body2">
            No report generated yet. <br />
            Press "Generate Report" button to create the report.
          </Typography>
        )}
      </Box>
    </>
  );
}
